import {
  candidateTenantsApi,
  type CandidateRentomaxLookupDto,
  type CreateCandidateTenantRequest,
  type CandidateInviteResultDto,
  type PropertyDto,
  type TenantDto,
  tenantsApi,
  type UpdateCandidateTenantRequest,
} from '../../services/api';
import { ApiError } from '../../services/core.api';
import { toastService } from '../../services/toast';
import {
  CLOSE_ICON,
  esc,
  formatRentomaxProvider,
  INFO_ICON,
  getCandidateStatuses,
  translateCandidateStatus,
} from './candidate-shared';
import { t } from '../../services/i18n';

type CandidateDialogOptions = {
  activePropertyId: string | null;
  candidate?: TenantDto;
  properties: PropertyDto[];
  onSaved: (propertyId?: string) => Promise<void>;
};

export async function openCandidateForm(options: CandidateDialogOptions): Promise<void> {
  const { activePropertyId, candidate, properties, onSaved } = options;
  const editing = candidate ?? null;
  const defaultPropertyId = editing?.propertyId ?? activePropertyId ?? '';
  const candidateStatuses = getCandidateStatuses();
  const knownPersons: TenantDto[] = [];
  let rentomaxLookup: CandidateRentomaxLookupDto | null = null;
  let rentomaxLookupLoading = false;
  let rentomaxLookupError = false;
  let lookupRequestId = 0;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <h2>${editing ? t('waitinglist.form.editTitle') : t('waitinglist.form.addTitle')}</h2>
        <button class="modal-close" data-action="cancel">
          ${CLOSE_ICON}
        </button>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${t('common.property')} *</label>
          <select name="propertyId" required>
            ${properties.map((property) =>
              `<option value="${property.id}" ${defaultPropertyId === property.id ? 'selected' : ''}>${esc(property.displayName || property.address)}</option>`).join('')}
          </select>
        </div>

        ${!editing ? `
          <div class="form-group">
            <span class="person-mode-label">${t('waitinglist.form.personLabel')}</span>
            <div class="person-mode-group" role="radiogroup" aria-label="${esc(t('waitinglist.form.personLabel'))}">
              <label class="person-mode-option" data-person-mode-option="new">
                <input class="person-mode-input" type="radio" name="personMode" value="new" checked>
                <span class="person-mode-card">
                  <span class="person-mode-indicator" aria-hidden="true"></span>
                  <span class="person-mode-copy">
                    <span class="person-mode-title">${t('waitinglist.form.personModeNewTitle')}</span>
                    <span class="person-mode-description">${t('waitinglist.form.personModeNewDescription')}</span>
                  </span>
                </span>
              </label>
              <label class="person-mode-option" data-person-mode-option="existing">
                <input class="person-mode-input" type="radio" name="personMode" value="existing">
                <span class="person-mode-card">
                  <span class="person-mode-indicator" aria-hidden="true"></span>
                  <span class="person-mode-copy">
                    <span class="person-mode-title">${t('waitinglist.form.personModeExistingTitle')}</span>
                    <span class="person-mode-description">${t('waitinglist.form.personModeExistingDescription')}</span>
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div id="new-person-fields">
            <div class="form-row">
              <div class="form-group">
                <label>${t('common.name')} *</label>
                <input name="firstName" required>
              </div>
              <div class="form-group">
                <label>${t('tenant.form.lastName')} *</label>
                <input name="lastName" required>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>${t('common.email')}</label>
                <input name="email" type="email">
              </div>
              <div class="form-group">
                <label>${t('common.phone')}</label>
                <input name="phone">
              </div>
            </div>
          </div>

          <div id="existing-person-fields" hidden>
            <div class="form-group">
              <label>${t('waitinglist.form.selectPerson')} *</label>
              <select name="tenantId">
                <option value="">${t('waitinglist.form.selectPlaceholder')}</option>
                <option value="" disabled>${t('waitinglist.form.loadingKnownPersons')}</option>
              </select>
            </div>
          </div>

          <div class="form-group" data-rentomax-section hidden></div>` : `
          <div class="form-row">
            <div class="form-group">
              <label>${t('common.name')} *</label>
              <input name="firstName" required value="${esc(editing.firstName)}">
            </div>
            <div class="form-group">
              <label>${t('tenant.form.lastName')} *</label>
              <input name="lastName" required value="${esc(editing.lastName)}">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>${t('common.email')}</label>
              <input name="email" type="email" value="${esc(editing.email || '')}">
            </div>
            <div class="form-group">
              <label>${t('common.phone')}</label>
              <input name="phone" value="${esc(editing.phone || '')}">
            </div>
          </div>`}

        <div class="form-group">
          <label>${t('waitinglist.form.interestDateLabel')} *</label>
          <input name="interestDate" type="date" required value="${editing?.interestDate || new Date().toISOString().slice(0, 10)}">
        </div>
        ${editing ? `
          <div class="form-group">
            <label>${t('common.status')}</label>
            <select name="status">
              ${candidateStatuses.map((status) => `<option value="${status.key}" ${editing.status === status.key ? 'selected' : ''}>${esc(status.label)}</option>`).join('')}
            </select>
          </div>` : ''}
        <div class="form-group">
          <label>${t('common.notes')}</label>
          <textarea name="notes" rows="3" placeholder="${t('waitinglist.form.notesPlaceholder')}">${esc(editing?.notes || '')}</textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${editing ? t('common.save') : t('common.add')}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  const getSelectedCandidateEmail = (): string => {
    const mode = (overlay.querySelector('input[name="personMode"]:checked') as HTMLInputElement | null)?.value ?? 'new';
    if (mode === 'existing') {
      const selectedTenantId = (overlay.querySelector('select[name="tenantId"]') as HTMLSelectElement | null)?.value;
      return knownPersons.find((person) => person.id === selectedTenantId)?.email?.trim() ?? '';
    }

    return (overlay.querySelector('input[name="email"]') as HTMLInputElement | null)?.value.trim() ?? '';
  };

  const renderRentomaxSection = (): void => {
    if (editing) return;

    const section = overlay.querySelector<HTMLElement>('[data-rentomax-section]');
    if (!section) return;

    const email = getSelectedCandidateEmail();
    if (!email) {
      section.hidden = false;
      section.innerHTML = `<div class="candidate-convert-note">${INFO_ICON}<span>${t('waitinglist.form.rentomaxLookupHint')}</span></div>`;
      return;
    }

    if (rentomaxLookupLoading) {
      section.hidden = false;
      section.innerHTML = `<div class="candidate-convert-note">${INFO_ICON}<span>${t('waitinglist.form.rentomaxLookupLoading')}</span></div>`;
      return;
    }

    if (rentomaxLookupError || !rentomaxLookup) {
      section.hidden = false;
      section.innerHTML = `<div class="candidate-convert-note">${INFO_ICON}<span>${t('waitinglist.form.rentomaxLookupError')}</span></div>`;
      return;
    }

    const hasAccount = rentomaxLookup.hasRentomaxAccount;
    const currentAction = (overlay.querySelector('input[name="rentomaxAction"]:checked') as HTMLInputElement | null)?.value ?? 'none';
    const note = hasAccount
      ? t('waitinglist.form.rentomaxKnownAccount', { provider: formatRentomaxProvider(rentomaxLookup.matchedRentomaxProvider) })
      : t('waitinglist.form.rentomaxNoAccount');
    const secondaryAction = hasAccount
      ? {
          value: 'link-existing',
          title: t('waitinglist.form.rentomaxAction.linkTitle'),
          description: t('waitinglist.form.rentomaxAction.linkDescription'),
        }
      : {
          value: 'invite-account',
          title: t('waitinglist.form.rentomaxAction.inviteTitle'),
          description: t('waitinglist.form.rentomaxAction.inviteDescription'),
        };

    section.hidden = false;
    section.innerHTML = `
      <div class="candidate-convert-note">
        ${INFO_ICON}
        <span>${note}</span>
      </div>
      <div class="form-group">
        <span class="person-mode-label">${t('waitinglist.form.rentomaxActionLabel')}</span>
        <div class="person-mode-group" role="radiogroup" aria-label="${esc(t('waitinglist.form.rentomaxActionLabel'))}">
          <label class="person-mode-option${currentAction === 'none' ? ' is-selected' : ''}">
            <input class="person-mode-input" type="radio" name="rentomaxAction" value="none" ${currentAction === 'none' ? 'checked' : ''}>
            <span class="person-mode-card">
              <span class="person-mode-indicator" aria-hidden="true"></span>
              <span class="person-mode-copy">
                <span class="person-mode-title">${t('waitinglist.form.rentomaxAction.noneTitle')}</span>
                <span class="person-mode-description">${hasAccount ? t('waitinglist.form.rentomaxAction.noneDescriptionKnown') : t('waitinglist.form.rentomaxAction.noneDescriptionUnknown')}</span>
              </span>
            </span>
          </label>
          <label class="person-mode-option${currentAction === secondaryAction.value ? ' is-selected' : ''}">
            <input class="person-mode-input" type="radio" name="rentomaxAction" value="${secondaryAction.value}" ${currentAction === secondaryAction.value ? 'checked' : ''}>
            <span class="person-mode-card">
              <span class="person-mode-indicator" aria-hidden="true"></span>
              <span class="person-mode-copy">
                <span class="person-mode-title">${secondaryAction.title}</span>
                <span class="person-mode-description">${secondaryAction.description}</span>
              </span>
            </span>
          </label>
        </div>
      </div>
    `;

    section.querySelectorAll('input[name="rentomaxAction"]').forEach((radio) => {
      radio.addEventListener('change', () => renderRentomaxSection());
    });
  };

  const refreshRentomaxLookup = async (): Promise<void> => {
    if (editing) return;

    const email = getSelectedCandidateEmail();
    if (!email) {
      rentomaxLookup = null;
      rentomaxLookupLoading = false;
      rentomaxLookupError = false;
      renderRentomaxSection();
      return;
    }

    const requestId = ++lookupRequestId;
    rentomaxLookupLoading = true;
    rentomaxLookupError = false;
    renderRentomaxSection();

    try {
      const result = await candidateTenantsApi.lookupRentomax(email);
      if (requestId !== lookupRequestId) return;
      rentomaxLookup = result;
    } catch {
      if (requestId !== lookupRequestId) return;
      rentomaxLookup = null;
      rentomaxLookupError = true;
    } finally {
      if (requestId !== lookupRequestId) return;
      rentomaxLookupLoading = false;
      renderRentomaxSection();
    }
  };

  if (!editing) {
    const syncPersonModeUi = () => {
      overlay.querySelectorAll('.person-mode-option').forEach((option) => {
        const input = option.querySelector('input[name="personMode"]') as HTMLInputElement | null;
        option.classList.toggle('is-selected', !!input?.checked);
      });
    };

    syncPersonModeUi();

    overlay.querySelectorAll('input[name="personMode"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        syncPersonModeUi();
        const isExisting = (overlay.querySelector('input[name="personMode"]:checked') as HTMLInputElement)?.value === 'existing';
        const newFields = overlay.querySelector('#new-person-fields') as HTMLElement;
        const existingFields = overlay.querySelector('#existing-person-fields') as HTMLElement;
        const firstNameInput = overlay.querySelector('input[name="firstName"]') as HTMLInputElement;
        const lastNameInput = overlay.querySelector('input[name="lastName"]') as HTMLInputElement;
        const tenantSelect = overlay.querySelector('select[name="tenantId"]') as HTMLSelectElement;

        newFields.hidden = isExisting;
        existingFields.hidden = !isExisting;
        firstNameInput.required = !isExisting;
        lastNameInput.required = !isExisting;
        if (tenantSelect) tenantSelect.required = isExisting;
        void refreshRentomaxLookup();
      });
    });

    overlay.querySelector('input[name="email"]')?.addEventListener('input', () => {
      void refreshRentomaxLookup();
    });
    overlay.querySelector('select[name="tenantId"]')?.addEventListener('change', () => {
      void refreshRentomaxLookup();
    });

    renderRentomaxSection();
  }

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitButton.disabled = true;
    submitButton.textContent = editing ? t('common.saving') : t('common.processing');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const propertyId = formData.get('propertyId') as string;

    try {
      if (editing) {
        const request: UpdateCandidateTenantRequest = {
          tenantId: editing.tenantId!,
          propertyId,
          firstName: formData.get('firstName') as string,
          lastName: formData.get('lastName') as string,
          email: (formData.get('email') as string) || undefined,
          phone: (formData.get('phone') as string) || undefined,
          interestDate: formData.get('interestDate') as string,
          notes: (formData.get('notes') as string) || undefined,
          status: formData.get('status') as string,
        };
        await candidateTenantsApi.update(editing.id, request);
      } else {
        const mode = (overlay.querySelector('input[name="personMode"]:checked') as HTMLInputElement)?.value ?? 'new';
        const rentomaxAction = (formData.get('rentomaxAction') as string) || undefined;
        const request: CreateCandidateTenantRequest = mode === 'existing'
          ? {
              tenantId: formData.get('tenantId') as string,
              propertyId,
              interestDate: formData.get('interestDate') as string,
              notes: (formData.get('notes') as string) || undefined,
              rentomaxAction,
            }
          : {
              firstName: formData.get('firstName') as string,
              lastName: formData.get('lastName') as string,
              email: (formData.get('email') as string) || undefined,
              phone: (formData.get('phone') as string) || undefined,
              propertyId,
              interestDate: formData.get('interestDate') as string,
              notes: (formData.get('notes') as string) || undefined,
              rentomaxAction,
            };
        await candidateTenantsApi.create(request);
      }

      close();
      await onSaved(propertyId);
    } catch (error) {
      console.error('Failed to save candidate:', error);
      const message = error instanceof ApiError ? error.message : t('waitinglist.form.saveError');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = message;
      }
      submitButton.disabled = false;
      submitButton.textContent = editing ? t('common.save') : t('common.add');
    }
  });

  document.body.appendChild(overlay);

  if (!editing) {
    const tenantSelect = overlay.querySelector('select[name="tenantId"]') as HTMLSelectElement | null;
    if (tenantSelect) {
      try {
        knownPersons.push(...await tenantsApi.getAll());
        tenantSelect.innerHTML = `
          <option value="">${t('waitinglist.form.selectPlaceholder')}</option>
          ${knownPersons.length > 0
            ? knownPersons.map((person) =>
              `<option value="${person.id}">${esc(person.firstName)} ${esc(person.lastName)}${person.email ? ` - ${esc(person.email)}` : ''}</option>`).join('')
            : `<option value="" disabled>${t('waitinglist.form.noKnownPersons')}</option>`}`;
        void refreshRentomaxLookup();
      } catch {
        tenantSelect.innerHTML = `
          <option value="">${t('waitinglist.form.selectPlaceholder')}</option>
          <option value="" disabled>${t('waitinglist.form.noKnownPersons')}</option>`;
      }
    }
  }
}

export async function openCandidateInviteDialog(candidate: TenantDto, onSent: () => Promise<void>): Promise<void> {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>${t('waitinglist.inviteDialogTitle')}</h2>
          <p class="modal-subtitle">${esc(candidate.firstName)} ${esc(candidate.lastName)}${candidate.email ? ` - ${esc(candidate.email)}` : ''}</p>
        </div>
        <button class="modal-close" data-action="cancel">
          ${CLOSE_ICON}
        </button>
      </div>
      <div class="candidate-convert-note">
        ${INFO_ICON}
        <span>${t('waitinglist.inviteDialogFreeNote')}</span>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group">
          <label>${t('waitinglist.inviteDialogMessageLabel')}</label>
          <textarea name="message" rows="5" placeholder="${t('waitinglist.inviteDialogMessagePlaceholder')}"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-primary">${candidate.invitePending ? t('waitinglist.inviteResendButton') : t('waitinglist.inviteSend')}</button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    submitButton.disabled = true;
    submitButton.textContent = t('waitinglist.inviteSending');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    try {
      const result = await candidateTenantsApi.sendInvite(candidate.id, (formData.get('message') as string) || undefined);
      close();
      await onSent();
      toastService.success(buildInviteSuccessMessage(result));
    } catch (error) {
      console.error('Failed to send candidate invite:', error);
      const message = error instanceof ApiError ? error.message : t('waitinglist.inviteError');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = message;
      }
      submitButton.disabled = false;
      submitButton.textContent = candidate.invitePending ? t('waitinglist.inviteResendButton') : t('waitinglist.inviteSend');
    }
  });

  document.body.appendChild(overlay);
}

function buildInviteSuccessMessage(result: CandidateInviteResultDto): string {
  return `${t('waitinglist.inviteSuccess')} ${result.inviteUrl}`;
}

export function openCandidateConvertDialog(candidate: TenantDto, onConverted: () => Promise<void>): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>${t('waitinglist.form.convertTitle', { status: translateCandidateStatus('Actief').toLowerCase() })}</h2>
          <p class="modal-subtitle">${esc(candidate.firstName)} ${esc(candidate.lastName)}</p>
        </div>
        <button class="modal-close" data-action="cancel">
          ${CLOSE_ICON}
        </button>
      </div>
      <div class="candidate-convert-note">
        ${INFO_ICON}
        <span>${t('waitinglist.form.convertDescription')}</span>
      </div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-row">
          <div class="form-group">
            <label>${t('common.startDate')} *</label>
            <input name="startDate" type="date" required value="${new Date().toISOString().slice(0, 10)}">
          </div>
          <div class="form-group">
            <label>${t('common.endDate')}</label>
            <input name="endDate" type="date">
          </div>
        </div>
        <div class="form-group">
          <label>${t('common.rentAmountPerMonth')} *</label>
          <input name="rentAmount" type="number" step="0.01" min="0.01" required placeholder="${t('common.rentAmountPlaceholder')}">
        </div>
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          <button type="submit" class="btn btn-sm btn-candidate-convert">
            ${t('waitinglist.form.convertSubmit', { status: translateCandidateStatus('Actief').toLowerCase() })}
          </button>
        </div>
      </form>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));

  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitButton = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitButton.disabled = true;
    submitButton.textContent = t('common.processing');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    const formData = new FormData(event.currentTarget as HTMLFormElement);

    try {
      await candidateTenantsApi.update(candidate.id, {
        tenantId: candidate.tenantId!,
        propertyId: candidate.propertyId,
        interestDate: candidate.interestDate!,
        notes: candidate.notes,
        status: 'Actief',
        startDate: formData.get('startDate') as string,
        endDate: (formData.get('endDate') as string) || undefined,
        rentAmount: parseFloat(formData.get('rentAmount') as string),
      });
      close();
      await onConverted();
    } catch (error) {
      console.error('Failed to convert candidate:', error);
      const message = error instanceof ApiError ? error.message : t('waitinglist.form.convertError');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = message;
      }
      submitButton.disabled = false;
      submitButton.textContent = t('waitinglist.form.convertSubmit', { status: translateCandidateStatus('Actief').toLowerCase() });
    }
  });

  document.body.appendChild(overlay);
}
